import * as React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@one-thd/sui-atomic-components';
import { ComponentProviders } from '../../core/ComponentProviders';
import { SweepstakesEntryCard } from './SweepstakesEntryCard';
import { SweepstakesDrawerContainer } from './SweepstakesDrawerContainer';
import { useSweepstakesDrawer } from '../../contexts';

/*

Prompt:
  - Create a SweepstakesDemo component that behaves similar to this SweepstakesDrawersExample,
    except you pass in the mock as a prop.
  - Everything else about how it's used should be a 1-liner
  - Place the component within src/components/Sweepstakes and export it thru loyalty-benefits.js
    so it can be used outside of this repo as a 1-liner
  - The only things that are needed from SweepstakesDrawersExample are:
    - ComponentProviders
    - SweepstakesEntryCard
    - SweepstakesDrawerContainer
    ..Not the typography stuff
  - Keep this comment within the file

*/

/*
  SweepstakesDemo
  - Accepts a `sweepstakesData` prop (mock or real) and renders
    the entry card + drawer container similar to the example.
  - Intentionally minimal so it can be used as a 1-liner export.
*/
function RenderSweepstakesDemo({ sweepstakesData, isLoading, hasError, onAcceptSweepstakes }) {
  const { openDrawer, DRAWER_TYPES } = useSweepstakesDrawer();

  const handleDetailsClick = (data) => {
    if (data && data.isOptedIn) {
      openDrawer(DRAWER_TYPES.DETAILS, { sweepstakesData: data });
    } else {
      openDrawer(DRAWER_TYPES.OPT_IN, { sweepstakesData: data });
    }
  };

  const handleStepClick = (step, data) => {
    openDrawer(DRAWER_TYPES.STEP, { selectedStep: step, sweepstakesData: data });
  };

  return (
    <>
      <SweepstakesEntryCard
        sweepstakesData={sweepstakesData}
        onDetailsClick={handleDetailsClick}
        onStepClick={handleStepClick}
        isLoading={isLoading}
        hasError={hasError}
      />
      <SweepstakesDrawerContainer
        isLoading={isLoading}
        hasError={hasError}
        onAcceptSweepstakes={onAcceptSweepstakes}
      />
    </>
  );
}

// Top-level component: ensures providers are mounted before hooks run
export function SweepstakesDemo(props) {
  const { sweepstakesData, isLoading, hasError, onAcceptSweepstakes } = props;

  return (
    <ComponentProviders componentName="SweepstakesDrawers">
      <RenderSweepstakesDemo
        sweepstakesData={sweepstakesData}
        isLoading={isLoading}
        hasError={hasError}
        onAcceptSweepstakes={onAcceptSweepstakes}
      />
    </ComponentProviders>
  );
}

SweepstakesDemo.propTypes = {
  sweepstakesData: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  onAcceptSweepstakes: PropTypes.func,
};

SweepstakesDemo.defaultProps = {
  sweepstakesData: null,
  isLoading: false,
  hasError: false,
  onAcceptSweepstakes: async () => ({ success: true }),
};

// Mirror propTypes/defaultProps on the inner render component for clarity
RenderSweepstakesDemo.propTypes = SweepstakesDemo.propTypes;
RenderSweepstakesDemo.defaultProps = SweepstakesDemo.defaultProps;

export default SweepstakesDemo;
